import { ShopifyBundles } from "shopify-bundles";

/**
 * Elegant Baby personalization bundle integration.
 *
 * Uses the reusable ShopifyBundles library to add
 * personalized products according to the theme setting.
*/
class ElegantBabyBundles {
    constructor() {
        this.bundles = new ShopifyBundles();
    }

    /**
     * Extract line item properties from Shopify form data.
     *
     * @param {FormData} formData
     * @returns {Object}
    */
    getProperties( formData ) {
        let result = {};

        for ( const entry of formData.entries() ) {
            const name = entry[0];
            const value = entry[1];

            if (
                name.startsWith( "properties[" ) &&
                name.endsWith( "]" )
            ) {
                const propertyName = name.substring( 11, name.length - 1 );

                if (
                    propertyName !== "_bundle_id" &&
                    propertyName !== "_bundle_role" &&
                    typeof value === "string" &&
                    value !== ""
                ) {
                    result[propertyName] = value;
                }
            }
        }

        return result;
    }

    /**
     * Return the bundle mode selected in Shopify Theme Settings.
     *
     * @returns {string}
    */
    getMode() {
        let result = "ajax_validation";

        if (
            window.ElegantBabyBundleConfig != null &&
            typeof window.ElegantBabyBundleConfig.mode === "string" &&
            window.ElegantBabyBundleConfig.mode !== ""
        ) {
            result = window.ElegantBabyBundleConfig.mode;
        }

        return result;
    }

    /**
     * Return the personalization variant selected in Theme Settings.
     *
     * @returns {number|null}
    */
    getPersonalizationVariantId() {
        let result = null;

        if (
            window.ElegantBabyBundleConfig != null &&
            window.ElegantBabyBundleConfig.personalizationVariantId != null
        ) {
            const variantId = Number( window.ElegantBabyBundleConfig.personalizationVariantId );

            if (
                Number.isSafeInteger( variantId ) &&
                variantId > 0
            ) {
                result = variantId;
            }
        }

        return result;
    }

    /**
     * Add a personalized product with its associated charge.
     *
     * Ajax mode adds the original product and a native nested
     * personalization child.
     *
     * Cart Transform mode adds only the parent product.
     *
     * @param {FormData} formData
     * @param {number} personalizationVariantId
     * @param {Object} options
     * @returns {Promise<Object>}
    */
    async addPersonalizedProduct( formData, personalizationVariantId, options = {} ) {
        let result = null;

        const properties = this.getProperties( formData );
        const variantId = Number( formData.get( "id" ) );
        const quantity = Number( formData.get( "quantity" ) || 1 );
        const sellingPlan = formData.get( "selling_plan" );
        const mode = this.getMode();

        const parent = {
            id: variantId,
            quantity: quantity,
            properties: properties
        };

        if ( sellingPlan !== null && sellingPlan !== "" ) {
            parent.selling_plan = sellingPlan;
        }

        if ( this.bundles.isValidItem( parent ) !== true ) {
            throw new Error( "A valid parent product variant is required." );
        }

        if (
            typeof properties.Personalization !== "string" ||
            properties.Personalization.trim() === ""
        ) {
            throw new Error( "A personalization selection is required." );
        }

        if ( mode === "ajax_validation" ) {
            const selectedVariantId = this.getPersonalizationVariantId();

            if ( selectedVariantId === null ) {
                throw new Error( "A personalization product must be selected in Theme Settings." );
            }

            const children = [
                {
                    id: selectedVariantId,
                    quantity: quantity
                }
            ];

            result = await this.bundles.addBundle( {
                parent: parent,
                children: children,
                sections: options.sections,
                sections_url: options.sections_url
            } );
        } else if ( mode === "cart_transform" ) {
            const selectedVariantId = this.getPersonalizationVariantId();

            if ( selectedVariantId === null ) {
                throw new Error( "A personalization product must be selected in Theme Settings." );
            }

            parent.properties._personalization_variant_id = String( selectedVariantId );

            result = await this.bundles.addSingleItem( parent, options );
        } else {
            throw new Error( "Invalid personalization bundle mode: " + mode );
        }

        return result;
    }
}

window.ElegantBabyBundles = new ElegantBabyBundles();